import { Controller } from "stimulus"

export default class extends Controller {
  connect() {
    console.log("Email composer controller connected")
  }

  // Generate AI-powered subject line suggestions
  async generateSubject(event) {
    event.preventDefault()

    const button = event.currentTarget
    const originalText = button.innerHTML

    // Show loading state
    button.disabled = true
    button.innerHTML = `
      <svg class="animate-spin mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Generating...
    `

    try {
      // Get current body content for context
      const summernoteElement = document.querySelector('[data-controller~="summernote"]')
      let bodyContent = ''

      if (summernoteElement) {
        const summernoteController = this.application.getControllerForElementAndIdentifier(summernoteElement, 'summernote')
        if (summernoteController) {
          bodyContent = summernoteController.getContent()
        }
      }

      const response = await fetch('/emails/generate_subject', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          context: bodyContent,
          template_type: 'custom'
        })
      })

      if (!response.ok) throw new Error('Failed to generate subjects')

      const data = await response.json()

      // Show suggestions using querySelector
      const suggestionsContainer = document.querySelector('[data-email-composer-target="suggestions"]')
      const suggestionsList = document.querySelector('[data-email-composer-target="suggestionsList"]')

      if (!data.subjects || data.subjects.length === 0) {
        toastr.warning('No suggestions generated. Please try again.')
        return
      }

      // Clear previous suggestions
      if (suggestionsList) {
        suggestionsList.innerHTML = ''

        // Add each suggestion
        data.subjects.forEach(subject => {
          const suggestionDiv = document.createElement('div')
          suggestionDiv.className = 'flex items-start p-3 rounded-md bg-white border border-gray-200 hover:border-primary-300 hover:bg-primary-50 cursor-pointer transition-colors'
          suggestionDiv.innerHTML = `
            <svg class="h-5 w-5 text-primary-600 mr-2 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
            <span class="text-sm text-gray-700 flex-1">${this.escapeHtml(subject)}</span>
          `

          suggestionDiv.addEventListener('click', () => {
            const subjectInput = document.getElementById('email_subject')
            if (subjectInput) {
              subjectInput.value = subject
            }
            if (suggestionsContainer) {
              suggestionsContainer.classList.add('hidden')
            }
          })

          suggestionsList.appendChild(suggestionDiv)
        })

        // Show the suggestions container
        if (suggestionsContainer) {
          suggestionsContainer.classList.remove('hidden')
        }
      }

    } catch (error) {
      console.error('Error generating subjects:', error)
      toastr.error('Failed to generate subject suggestions. Please try again.')
    } finally {
      // Restore button
      button.disabled = false
      button.innerHTML = originalText
    }
  }

  // Improve email content with AI
  async improveContent(event) {
    event.preventDefault()

    const button = event.currentTarget
    const originalText = button.innerHTML

    // Get the Summernote editor
    const summernoteElement = document.querySelector('[data-controller~="summernote"]')

    if (!summernoteElement) {
      toastr.error('Could not find email body field.')
      return
    }

    // Get Summernote controller and content
    const summernoteController = this.application.getControllerForElementAndIdentifier(summernoteElement, 'summernote')
    if (!summernoteController) {
      toastr.error('Could not initialize editor.')
      return
    }

    const currentContent = summernoteController.getContent()

    // Strip HTML tags to check actual text content
    const textContent = this.stripHtml(currentContent)

    // Check if there's actual text content to improve (not just empty HTML tags)
    if (!textContent || textContent.trim().length === 0) {
      toastr.warning('Please write some content first before using AI improvement.')
      return
    }

    // Show loading state
    button.disabled = true
    button.innerHTML = `
      <svg class="animate-spin mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Improving...
    `

    try {
      const response = await fetch('/emails/improve_content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          current_content: currentContent,
          goal: 'engagement'
        })
      })

      if (!response.ok) throw new Error('Failed to improve content')

      const data = await response.json()

      // Get the HTML content from AI
      let htmlContent = data.content

      // Remove any markdown code fences if AI includes them
      htmlContent = htmlContent.replace(/```html\s*/gi, '')
      htmlContent = htmlContent.replace(/```\s*$/g, '')
      htmlContent = htmlContent.trim()

      // Log the HTML content for debugging
      console.log('HTML content from AI:', htmlContent)

      // Insert the improved HTML content into Summernote
      if (summernoteController) {
        summernoteController.setContent(htmlContent)
      }

      // Show success message
      toastr.success('Content has been improved! You can now edit it visually or switch to Code View.')

    } catch (error) {
      console.error('Error improving content:', error)
      toastr.error('Failed to improve content. Please try again.')
    } finally {
      // Restore button
      button.disabled = false
      button.innerHTML = originalText
    }
  }

  // Show subject line suggestions
  showSuggestions(subjects) {
    if (!subjects || subjects.length === 0) {
      toastr.warning('No suggestions generated. Please try again.')
      return
    }

    const suggestionsListElement = document.querySelector('[data-email-composer-target="suggestionsList"]')
    const suggestionsElement = document.querySelector('[data-email-composer-target="suggestions"]')

    if (!suggestionsListElement) return

    // Clear previous suggestions
    suggestionsListElement.innerHTML = ''

    // Add each suggestion as a clickable option
    subjects.forEach(subject => {
      const suggestionDiv = document.createElement('div')
      suggestionDiv.className = 'flex items-start p-3 rounded-md bg-white border border-gray-200 hover:border-primary-300 hover:bg-primary-50 cursor-pointer transition-colors'
      suggestionDiv.innerHTML = `
        <svg class="h-5 w-5 text-primary-600 mr-2 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
        </svg>
        <span class="text-sm text-gray-700 flex-1">${this.escapeHtml(subject)}</span>
      `

      suggestionDiv.addEventListener('click', () => {
        this.selectSuggestion(subject)
      })

      suggestionsListElement.appendChild(suggestionDiv)
    })

    // Show the suggestions container
    if (suggestionsElement) {
      suggestionsElement.classList.remove('hidden')
    }
  }

  // Select a suggestion
  selectSuggestion(subject) {
    const subjectInput = document.getElementById('email_subject')
    if (subjectInput) {
      subjectInput.value = subject
    }
    this.hideSuggestions()
  }

  // Hide suggestions
  hideSuggestions() {
    const suggestionsElement = document.querySelector('[data-email-composer-target="suggestions"]')
    if (suggestionsElement) {
      suggestionsElement.classList.add('hidden')
    }
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Strip HTML tags and convert to plain text
  stripHtml(html) {
    const div = document.createElement('div')
    div.innerHTML = html
    // Get text content and preserve line breaks
    let text = div.textContent || div.innerText || ''
    // Replace multiple spaces with single space
    text = text.replace(/\s+/g, ' ')
    // Trim
    return text.trim()
  }

  // Copy merge tag to clipboard
  copyMergeTag(event) {
    event.preventDefault()

    const button = event.currentTarget
    const tag = button.dataset.tag

    // Create a temporary textarea element
    const textarea = document.createElement('textarea')
    textarea.value = tag
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()

    try {
      // Copy using execCommand (more compatible)
      const successful = document.execCommand('copy')

      if (successful) {
        // Visual feedback
        const originalHTML = button.innerHTML
        const originalClasses = button.className

        button.innerHTML = `
          <svg class="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
          </svg>
          <span class="ml-1">Copied!</span>
        `
        button.className = "inline-flex items-center bg-green-50 px-1.5 py-1.5 rounded text-sm font-semibold text-green-700 border border-green-300"

        // Reset after 2 seconds
        setTimeout(() => {
          button.innerHTML = originalHTML
          button.className = originalClasses
        }, 2000)
      } else {
        throw new Error('Copy command failed')
      }
    } catch (error) {
      console.error('Failed to copy:', error)
      toastr.error('Failed to copy merge tag.')
    } finally {
      // Remove the temporary textarea
      document.body.removeChild(textarea)
    }
  }

  // Validate form before submission
  submit(event) {
    const subjectInput = document.getElementById('email_subject')
    const bodyInput = document.getElementById('email_body')

    const subject = subjectInput ? subjectInput.value.trim() : ''
    const body = bodyInput ? bodyInput.value.trim() : ''

    if (!subject || !body) {
      event.preventDefault()
      toastr.warning("Please enter both a subject and message before sending.")
      return false
    }

    return true
  }

  // Save current email as template
  async saveAsTemplate(event) {
    event.preventDefault()

    // Get the Summernote editor
    const summernoteElement = document.querySelector('[data-controller~="summernote"]')

    if (!summernoteElement) {
      toastr.error('Could not find email body field.')
      return
    }

    const summernoteController = this.application.getControllerForElementAndIdentifier(summernoteElement, 'summernote')
    if (!summernoteController) {
      toastr.error('Could not initialize editor.')
      return
    }

    // Get subject and body
    const subjectInput = document.getElementById('email_subject')
    const subject = subjectInput ? subjectInput.value.trim() : ''
    const body = summernoteController.getContent()

    // Validate
    if (!subject || !body.trim()) {
      toastr.warning('Please enter both a subject and message before saving as a template.')
      return
    }

    // Show modal for template name
    this.showTemplateNameModal(subject, body)
  }

  // Show modal to input template name
  showTemplateNameModal(subject, body) {
    // Create modal
    const modal = document.createElement('div')
    modal.className = 'fixed inset-0 z-50 overflow-y-auto'
    modal.setAttribute('aria-labelledby', 'modal-title')
    modal.setAttribute('role', 'dialog')
    modal.setAttribute('aria-modal', 'true')
    modal.innerHTML = `
      <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 transition-opacity bg-gray-500/75" aria-hidden="true"></div>

        <!-- Center modal -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div>
            <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100">
              <svg class="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-5">
              <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                Save as Template
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  Enter a name for this email template. You can reuse it later for other campaigns.
                </p>
              </div>
              <div class="mt-4">
                <input type="text"
                       id="template-name-input"
                       value="${this.escapeHtml(subject)}"
                       placeholder="e.g., Order Confirmation Email"
                       class="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6">
              </div>
            </div>
          </div>
          <div class="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
            <button type="button"
                    id="save-template-btn"
                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm">
              Save Template
            </button>
            <button type="button"
                    id="cancel-template-btn"
                    class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm">
              Cancel
            </button>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(modal)

    // Focus on input
    const input = document.getElementById('template-name-input')
    setTimeout(() => input.focus(), 100)

    // Handle Enter key
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        document.getElementById('save-template-btn').click()
      }
    })

    // Handle Escape key
    document.addEventListener('keydown', function escapeHandler(e) {
      if (e.key === 'Escape') {
        document.body.removeChild(modal)
        document.removeEventListener('keydown', escapeHandler)
      }
    })

    // Save button handler
    document.getElementById('save-template-btn').addEventListener('click', async () => {
      const templateName = input.value.trim()

      if (!templateName) {
        toastr.warning('Please enter a template name.')
        input.focus()
        return
      }

      // Close modal
      document.body.removeChild(modal)

      // Save template
      await this.performSaveTemplate(templateName, subject, body)
    })

    // Cancel button handler
    document.getElementById('cancel-template-btn').addEventListener('click', () => {
      document.body.removeChild(modal)
    })

    // Click outside to close
    modal.querySelector('.bg-gray-500').addEventListener('click', () => {
      document.body.removeChild(modal)
    })
  }

  // Perform the actual save operation
  async performSaveTemplate(templateName, subject, body) {
    try {
      const response = await fetch('/emails/save_as_template', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          name: templateName,
          subject: subject,
          body: body,
          template_type: 'custom',
          ai_generated: false
        })
      })

      // Log response details for debugging
      console.log('Response status:', response.status)
      console.log('Response headers:', response.headers.get('content-type'))

      // Check if response is JSON
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text()
        console.error('Non-JSON response received:', text.substring(0, 500))
        throw new Error('Server returned an invalid response. Please check the browser console for details.')
      }

      const data = await response.json()

      if (response.ok && data.success) {
        toastr.success(`Template "${data.template_name}" saved successfully!`)
      } else {
        throw new Error(data.message || 'Failed to save template')
      }

    } catch (error) {
      console.error('Error saving template:', error)
      toastr.error('Failed to save template. ' + error.message)
    }
  }

  // Show HTML preview modal
  showHtmlPreview(htmlContent) {
    // Create modal overlay
    const modal = document.createElement('div')
    modal.className = 'fixed inset-0 z-50 overflow-y-auto'
    modal.innerHTML = `
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 transition-opacity bg-gray-500/75" aria-hidden="true"></div>

        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <!-- Header -->
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                AI-Generated HTML Email Template
              </h3>
              <button id="close-preview" class="text-gray-400 hover:text-gray-500">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Tabs -->
            <div class="border-b border-gray-200 mb-4">
              <nav class="-mb-px flex space-x-8">
                <button id="tab-preview" class="border-primary-500 text-primary-600 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm">
                  Preview
                </button>
                <button id="tab-code" class="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm">
                  HTML Code
                </button>
              </nav>
            </div>

            <!-- Preview Tab -->
            <div id="preview-content" class="border border-gray-300 rounded-lg overflow-auto" style="height: 500px; background: #f9fafb;">
              <iframe id="html-preview-frame" class="w-full h-full" style="background: white;"></iframe>
            </div>

            <!-- Code Tab (hidden by default) -->
            <div id="code-content" class="hidden border border-gray-300 rounded-lg overflow-auto" style="height: 500px;">
              <pre class="p-4 text-xs bg-gray-50"><code id="html-code" class="language-html"></code></pre>
            </div>
          </div>

          <!-- Footer -->
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button id="use-template" type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm">
              Use This Template
            </button>
            <button id="copy-html" type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
              Copy HTML
            </button>
            <button id="cancel-preview" type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm">
              Cancel
            </button>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(modal)

    // Load HTML into iframe for preview
    const iframe = document.getElementById('html-preview-frame')
    iframe.srcdoc = htmlContent

    // Display HTML code
    const codeElement = document.getElementById('html-code')
    codeElement.textContent = htmlContent

    // Tab switching
    const tabPreview = document.getElementById('tab-preview')
    const tabCode = document.getElementById('tab-code')
    const previewContent = document.getElementById('preview-content')
    const codeContent = document.getElementById('code-content')

    tabPreview.addEventListener('click', () => {
      tabPreview.className = 'border-primary-500 text-primary-600 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
      tabCode.className = 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
      previewContent.classList.remove('hidden')
      codeContent.classList.add('hidden')
    })

    tabCode.addEventListener('click', () => {
      tabCode.className = 'border-primary-500 text-primary-600 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
      tabPreview.className = 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
      codeContent.classList.remove('hidden')
      previewContent.classList.add('hidden')
    })

    // Use Template button
    document.getElementById('use-template').addEventListener('click', () => {
      const bodyElement = document.getElementById('email_body')
      if (bodyElement) {
        bodyElement.value = htmlContent
      }
      document.body.removeChild(modal)
      toastr.success('HTML template has been loaded into the email body field.')
    })

    // Copy HTML button
    document.getElementById('copy-html').addEventListener('click', () => {
      navigator.clipboard.writeText(htmlContent).then(() => {
        const btn = document.getElementById('copy-html')
        const originalText = btn.textContent
        btn.textContent = 'Copied!'
        toastr.success('HTML code copied to clipboard!')
        setTimeout(() => {
          btn.textContent = originalText
        }, 2000)
      }).catch(() => {
        toastr.error('Failed to copy to clipboard.')
      })
    })

    // Close buttons
    document.getElementById('close-preview').addEventListener('click', () => {
      document.body.removeChild(modal)
    })

    document.getElementById('cancel-preview').addEventListener('click', () => {
      document.body.removeChild(modal)
    })
  }
}
